<!-- オペレータログイン画面 -->

<template>
  <div class="whole-wrap whole-wrap--login">
    <!-- ログイン -->
    <div class="login">
      <!-- 言語選択 -->
      <div class="language-selector" v-click-outside="closeDropdown">
        <button 
          type="button" 
          class="language-button" 
          @click="toggleDropdown"
          :aria-expanded="isDropdownOpen"
        >
          <img src="@/assets/img/world.png" alt="Language" class="language-icon" />
          <span class="language-text">{{ selectedLocale === 'ja' ? 'JP' : 'EN' }}</span>
        </button>
        <div v-if="isDropdownOpen" class="language-dropdown">
          <div 
            class="language-option" 
            :class="{ 'language-option--active': selectedLocale === 'ja' }"
            @click="selectLanguage('ja')"
          >
            JP・日本語
          </div>
          <div 
            class="language-option" 
            :class="{ 'language-option--active': selectedLocale === 'en' }"
            @click="selectLanguage('en')"
          >
            EN・English
          </div>
        </div>
      </div>
      <div class="login-img"></div>
      <div class="login-system-name">{{ $t('app.title') }}</div>
      <div class="login-main">
        <!-- ログインID入力フォーム -->
        <div class="form-label">{{ $t('auth.login_id') }}</div>
        <input type="text" class="input-text mt-8" v-model="operatorId" id="login-id" name="login_id" />
        <!-- パスワード入力フォーム -->
        <div class="form-label mt-17">{{ $t('auth.password') }}</div>
        <input type="password" class="input-text mt-8" v-model="operatorPassword" id="login-password" name="login_password" />
        <!-- ID、パスワードがわからない場合のリンク -->
        <div class="btn login-not-clear" @click="toggleModal">{{ $t('auth.forgot_id_password') }}</div>

        <!-- テスト用 -->
        <!-- <div @click="openReport">reportテスト</div> -->
      </div>
      <!-- ログインボタン -->
      <div class="btn btn-base login-button" @click="operatorLogin">{{ $t('auth.login') }}</div>
      <!-- エラーメッセージ表示 -->
      <div class="error-message mt-20" v-if="loginErrorMessage" v-html="loginErrorMessage"></div>
      <div class="login-agreement mt-16">
        {{ $t('app.tos_notice1') }}<span class="login-agreement login-agreement--click" @click="agreementClick">{{ $t('app.tos_notice_link') }}</span><span v-html="$t('app.tos_notice2')"></span>
      </div>
      <div class="text-copyright-login">© 2025 Lion Corporation. All rights reserved.</div>
    </div>

    <!-- モーダル -->
    <div v-if="isModalVisible" ref="modal" class="modal-wrap">
      <div class="modal-overlay"></div>
      <div class="modal modal--not-clear-password">
        <div class="btn modal-close" @click="toggleModal"></div>
        <div class="modal-title">{{ $t('dialog_forgot.title') }}</div>
        <div class="modal-text mt-30">{{ $t('dialog_forgot.body_line') }}</div>
        <div class="modal-inquiry-email mt-68">{{ $t('dialog_forgot.contact_email') }}</div>
        <a class="modal-email mt-10" href="mailto:cs-ptk@lion.co.jp?subject=【口腔機能アプリ】問合せ">cs-ptk@lion.co.jp</a>
        <!-- <div class="modal-email mt-10">cs-ptk@lion.co.jp</div> -->
        <div class="flex justify-content-center mt-57">
          <div id="modal-close" class="btn btn-outline-base" @click="toggleModal">{{ $t('dialog_forgot.close') }}</div>
        </div>
      </div>
      <!-- コピーライト -->
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import * as Paths from '@/components/common/paths';
import { checkStringInput, checkPasswordInput } from '@/components/common/inputValidations';

export default {
  directives: {
    'click-outside': {
      mounted(el, binding) {
        el.clickOutsideEvent = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value();
          }
        };
        document.addEventListener('click', el.clickOutsideEvent);
      },
      unmounted(el) {
        document.removeEventListener('click', el.clickOutsideEvent);
      },
    },
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const { locale } = useI18n();
    const operatorId = ref('');
    const operatorPassword = ref('');
    const isModalVisible = ref(false);
    const loginErrorMessage = ref('');
    
    // 言語選択
    const selectedLocale = ref(locale.value);
    const isDropdownOpen = ref(false);
    
    // ドロップダウンの開閉
    const toggleDropdown = () => {
      isDropdownOpen.value = !isDropdownOpen.value;
    };
    
    const closeDropdown = () => {
      isDropdownOpen.value = false;
    };
    
    // 言語選択
    const selectLanguage = (newLocale) => {
      locale.value = newLocale;
      selectedLocale.value = newLocale;
      localStorage.setItem('app-locale', newLocale);
      isDropdownOpen.value = false;
      // CSSクラスも更新
      document.documentElement.setAttribute('lang', newLocale);
      const appElement = document.getElementById('app');
      if (appElement) {
        appElement.className = `lang-${newLocale}`;
      }
    };
    
    // 初期化時にlocalStorageから言語を読み込む
    onMounted(() => {
      const savedLocale = localStorage.getItem('app-locale');
      if (savedLocale && (savedLocale === 'ja' || savedLocale === 'en')) {
        locale.value = savedLocale;
        selectedLocale.value = savedLocale;
        document.documentElement.setAttribute('lang', savedLocale);
        const appElement = document.getElementById('app');
        if (appElement) {
          appElement.className = `lang-${savedLocale}`;
        }
      }
    });

    // モーダルの表示と非表示を切り替えるメソッド
    const toggleModal = () => {
      isModalVisible.value = !isModalVisible.value;
    };

    // ログイン処理
    const operatorLogin = async () => {
      // 入力のバリデーションを行います
      if (validateInputs()) {
        // 被検査者情報とオペレータ情報をクリア
        await store.dispatch('dataManagement/clearSubject');
        await store.dispatch('dataManagement/clearOperator');
        await store.dispatch('dataManagement/clearAuth');

        // ログインに必要なデータを構築します
        const loginData = {
          operatorId: operatorId.value,
          operatorPassword: operatorPassword.value,
        };
        // ログインアクションを実行し、結果を待ちます
        const loginResult = await store.dispatch('operatorLogin/login', loginData);
        await store.dispatch('dataManagement/fetch', loginResult.operatorId);
        // ログインが成功した場合
        if (loginResult.success) {
          // 設定画面にリダイレクトします
          router.push(`${Paths.SETTING_PATH}${loginResult.operatorId}`);
        } else {
          // ログインエラーメッセージを表示します
          loginErrorMessage.value = loginResult.error;
        }
      }
    };

    // 入力値のバリデーションチェック
    const validateInputs = () => {
      loginErrorMessage.value = '';

      const idError = checkStringInput(operatorId.value);
      const passwordError = checkPasswordInput(operatorPassword.value);

      // ID がエラーの場合
      if (idError) {
        loginErrorMessage.value += (loginErrorMessage.value ? '<br>' : '') + `ID:${idError}`;
      }
      // パスワードがエラーの場合
      if (passwordError) {
        loginErrorMessage.value += (loginErrorMessage.value ? '<br>' : '') + `Pass:${passwordError}`;
      }
      // エラーがあるかどうかを返す
      return !idError && !passwordError;
    };

    const agreementClick = async() => {
      // 利用規約画面へ遷移
      await router.push({
        name: 'agreement',
      });
    };

    //TODO: 当関数は最終的に削除する
    //結果レポートを開く
    const openReport = async ()=> {
      const reportData = {
          operatorId: 'abc', 
          subjectId: '1234', 
          measurementType: 1,
          audioStorageFlag: false,
          analysisType: '2',
          gender: 1,
          age: 50,
          measurements: [{type: 'pa', status: '実施済み'}, {type: 'ta', status: '実施済み'}, {type: 'ka', status: '実施済み'}, ],
          endDateTime: new Date(),
          graphData: [
            {
              audioId: 'abcd',
              type: 'pa',
              originalData: [],
              durationMean: 0.11236174242424243,
              vocabCount: 68,
              vocabPerSecond: 8.499826385962546,
              analysisVocabCount:34,
              vocabCountSecond: [null, 1, 3, 4, 6, 8, 8, 8, 6, 4, 3, null, null], 
              dtwCountSecond: [null, 0, 2, 2, 3, 5, 8, 8, 3, 1, 2, null, null], 
            },
            {
              audioId: 'abcd',
              type: 'ta',
              originalData: [],
              durationMean: 0.11236174242424243,
              vocabCount: 62,
              vocabPerSecond: 8.299826385962546,
              analysisVocabCount:45,
              vocabCountSecond: [null, 1, 3, 4, 6, 8, 8, 8, 6, 4, 3, null, null], 
              dtwCountSecond: [null, 1, 2, 3, 4, 7, 7, 8, 6, 4, 3, null, null], 
            },
            {
              audioId: 'abcd',
              type: 'ka',
              originalData: [],
              durationMean: 0.11236174242424243,
              vocabCount: 66,
              vocabPerSecond: 7.899826385962546,
              analysisVocabCount:45,
              vocabCountSecond: [null, 1, 3, 4, 6, 8, 8, 8, 6, 4, 3, null, null], 
              dtwCountSecond: [null, 1, 2, 3, 4, 7, 7, 8, 6, 4, 3, null, null], 
            }
          ],
        }

      await store.dispatch('dataManagement/setResultReportData', reportData);

      await router.push({
        name: 'result',
        params: {operator_id: 'abc'},
      });

    };

    return {
      operatorId,
      operatorPassword,
      loginErrorMessage,
      isModalVisible,
      selectedLocale,
      isDropdownOpen,
      toggleModal,
      toggleDropdown,
      closeDropdown,
      selectLanguage,
      operatorLogin,
      agreementClick,
      openReport,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '@/scss/style.scss';

.error-message {
  color: red;
}
</style>